/* ==========================================================================
   Skeleton Component
   Task F27: Loading state component with shimmer animation
   Based on design.md specifications
   ========================================================================== */

import { type HTMLAttributes, forwardRef } from 'react'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Skeleton variant for different content types */
  variant?: 'text' | 'avatar' | 'card' | 'table-row' | 'input' | 'button'
  /** Width - can be a Tailwind class or CSS value */
  width?: string
  /** Height - can be a Tailwind class or CSS value */
  height?: string
  /** Whether to show shimmer animation */
  animate?: boolean
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

/**
 * Skeleton component for loading states.
 * Shows a shimmer animation to indicate content is loading.
 *
 * @example
 * ```tsx
 * // Text skeleton
 * <Skeleton variant="text" width="w-48" />
 *
 * // Avatar skeleton
 * <Skeleton variant="avatar" />
 *
 * // Card skeleton
 * <Skeleton variant="card" />
 *
 * // Table row skeleton
 * <Skeleton variant="table-row" />
 *
 * // Custom dimensions
 * <Skeleton width="200px" height="40px" />
 * ```
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      variant = 'text',
      width,
      height,
      animate = true,
      className = '',
      ...props
    },
    ref
  ) => {
    // Base styles for shimmer effect per design.md
    const baseStyles = [
      'bg-gradient-to-r from-[var(--color-gray-200)] via-[var(--color-gray-100)] to-[var(--color-gray-200)]',
      'bg-[length:200%_100%]',
      animate && 'animate-shimmer',
    ]

    // Variant-specific styles
    const variantStyles: Record<string, string> = {
      text: 'h-4 rounded',
      avatar: 'w-10 h-10 rounded-full',
      card: 'h-32 rounded-lg',
      'table-row': 'h-14 rounded',
      input: 'h-10 rounded-md',
      button: 'h-10 rounded-md',
    }

    // Build className
    const classes = [
      ...baseStyles,
      variantStyles[variant],
      width,
      height,
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return <div ref={ref} className={classes} {...props} />
  }
)

Skeleton.displayName = 'Skeleton'

/* --------------------------------------------------------------------------
   Preset Skeleton Compositions
   -------------------------------------------------------------------------- */

/**
 * Skeleton for text lines with varying widths
 */
export function SkeletonText({
  lines = 3,
  className = '',
}: {
  lines?: number
  className?: string
}) {
  // Last line is typically shorter
  const widths = ['w-full', 'w-full', 'w-3/4', 'w-1/2', 'w-2/3']

  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          width={widths[i % widths.length]}
        />
      ))}
    </div>
  )
}

/**
 * Skeleton for a form input with label
 */
export function SkeletonFormField({ className = '' }: { className?: string }) {
  return (
    <div className={`space-y-2 ${className}`}>
      <Skeleton variant="text" width="w-24" height="h-3" />
      <Skeleton variant="input" width="w-full" />
    </div>
  )
}

/**
 * Skeleton for avatar with text
 */
export function SkeletonAvatarWithText({
  className = '',
}: {
  className?: string
}) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <Skeleton variant="avatar" />
      <div className="flex-1 space-y-2">
        <Skeleton variant="text" width="w-32" />
        <Skeleton variant="text" width="w-24" height="h-3" />
      </div>
    </div>
  )
}

/**
 * Skeleton for a table with header and rows
 */
export function SkeletonTable({
  rows = 5,
  columns = 4,
  className = '',
}: {
  rows?: number
  columns?: number
  className?: string
}) {
  return (
    <div className={`space-y-3 ${className}`}>
      {/* Header row */}
      <div className="flex gap-4 pb-2 border-b border-[var(--color-gray-200)]">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton
            key={`header-${i}`}
            variant="text"
            width="w-24"
            height="h-3"
          />
        ))}
      </div>
      {/* Data rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={`row-${rowIndex}`}
          className="flex gap-4 py-3 border-b border-[var(--color-gray-100)]"
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton
              key={`cell-${rowIndex}-${colIndex}`}
              variant="text"
              width={colIndex === 0 ? 'w-40' : 'w-24'}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Skeleton for a card with content
 */
export function SkeletonCard({
  showHeader = true,
  lines = 3,
  className = '',
}: {
  showHeader?: boolean
  lines?: number
  className?: string
}) {
  return (
    <div
      className={`bg-white rounded-lg shadow-sm p-6 space-y-4 ${className}`}
    >
      {showHeader && (
        <Skeleton variant="text" width="w-40" height="h-6" />
      )}
      <SkeletonText lines={lines} />
    </div>
  )
}

export default Skeleton
